"use client";

import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  useStudio,
  useStudioEngagement,
  useTonality,
  useBpm,
  useKeyboardEnabled,
  useMicToggle,
  useMidiInputs,
  useMidiToggle,
  useMetronomeAccent,
  useMetronomeDivision,
  useMetronomeEnabled,
  useMetronomeVolume,
  usePlaybackState,
  useTimeSignature,
} from "@/contexts/studio-context";
import type { Tonality } from "@/lib/music/tonality";
import type { Signature } from "@/lib/studio/studio";
import { recordPlay } from "@/lib/analytics/studio-session";
import { IconButton } from "../../atoms/icon-button";
import {
  ArrowLeftIcon,
  SettingsIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
} from "../../atoms/icons";
import { TonalityPicker } from "../../molecules/tonality-picker";
import { TransportControls } from "../../molecules/transport-controls";
import { useReplay } from "@/contexts/replay-context";
import { StudioModeToggle, type StudioModeValue } from "../../molecules/studio-mode-toggle";
import { TempoMetronomePopover } from "../../molecules/tempo-metronome-popover";
import { DeviceStatusBadge } from "../../atoms/device-status-badge";
import { GoLiveButton, LiveJoinError } from "@/components/live/go-live-button";
import { BackToLiveButton } from "@/components/live/back-to-live-button";
import { LobbyButton } from "@/components/live/lobby";
import { useLiveSession } from "@/contexts/live-session-context";

type SongInfo = {
  title: string | null;
  timeSignature: string;
  /**
   * Structured form of `timeSignature`, present whenever a piece is loaded.
   * Optional for backward compatibility with callers that only know the
   * string form.
   */
  timeSignatureSig?: Signature;
  bpm: number;
  difficulty?: number;
};

type ToolbarProps = {
  songInfo?: SongInfo | null;
  /** Tonality detected on the loaded piece (drives the transpose UI). */
  sourceTonality?: Tonality | null;
  /** Called when the user applies a transposition to the loaded piece. */
  onTranspose?: (target: Tonality) => Promise<void> | void;
  /** Whether the loaded piece is a generated exercise (drives the back button). */
  isGenerated?: boolean;
  /** Returns to the previous (non-generated) piece. Hidden when not applicable. */
  onBack?: () => void;
  /** Opens the studio settings overlay. Owned by `StudioPage` since the overlay
   *  lives inside `StudioFrame.overlay`. */
  onOpenSettings?: () => void;
  /**
   * Ouvre la fiche du morceau sur le choix du parcours.
   *
   * La bascule « Apprendre » de cette barre n'engage aucun parcours : elle
   * demande l'entrée du programme, qui est un état de la fiche et non un écran
   * d'ici.
   * Absente, la bascule ne fait rien de ce côté, ce qui est le cas quand aucun
   * morceau n'est chargé.
   */
  onEnterProgram?: () => void;
  /** Current expanded state of the left sidebar (drives the toggle glyph). */
  sidebarOpen?: boolean;
  /** Toggles the left sidebar. When omitted the toggle button is hidden. */
  onToggleSidebar?: () => void;
  /**
   * Le bandeau de séance est-il déployé, et comment le basculer.
   *
   * Relayés plutôt que tenus ici : le bandeau partage son logement avec la
   * génération d'exercice, et seul `StudioPage` peut tenir l'exclusion entre
   * les deux. La barre ne fait que porter la poignée.
   */
  livePanelOpen?: boolean;
  onToggleLivePanel?: (open: boolean) => void;
  /**
   * Spectateur d'un récital : la barre ne pilote plus rien.
   *
   * Ni bascule de mode, ni lecture, ni tempo, ni métronome, ni retour au
   * morceau, ni panneau latéral : le transport est celui de l'interprète, et
   * un bouton qui demanderait autre chose se ferait corriger par la métadonnée
   * suivante — ou ouvrirait une porte par laquelle un morceau entre. Le titre
   * et la tonalité restent, on regarde une pièce ; la poignée du panneau live
   * aussi, c'est par là qu'on quitte.
   */
  spectator?: boolean;
};

/**
 * Ce qu'un téléphone ne garde pas devant un replay.
 *
 * **En CSS, et pas en JS.** La moitié « téléphone » de la condition est une
 * requête média, et un hook de media query répond `false` au premier rendu :
 * branché dessus, un poste de bureau peindrait une frame sans ses commandes
 * avant de les rendre. Le CSS est juste dès le premier pixel. L'autre moitié —
 * « c'est un replay » — se décide bien en JS, mais elle ne clignote pas : elle
 * se lit dans l'URL dès le premier rendu.
 *
 * **`hidden` et pas `opacity-0`** : `display:none` sort aussi du parcours
 * clavier et de l'arbre d'accessibilité, ce qu'on veut — une commande retirée
 * ne doit pas rester annoncée à un lecteur d'écran.
 *
 * La variante `handheld:` couvre les deux orientations et nomme le pointeur ;
 * elle est définie une fois dans `app/globals.css`, où le raisonnement est
 * écrit. Debout, ce n'est plus un luxe mais une nécessité : à 390 px de large,
 * la barre d'outils complète déborde — le ton se coupait en plein mot et le
 * tempo se réduisait à un caractère.
 *
 * Ne sert qu'à **retirer**. Le chemin inverse — montrer sur téléphone ce que
 * le bureau n'a pas — passe par le JS : voir `replayDrawerHandle` plus bas.
 */
const HANDHELD_HIDDEN = "handheld:hidden";

/**
 * Inline metronome icon (triangle + balance bar).
 * Lucide-react has no `Metronome` glyph; this stays semantically correct
 * (cf. spec, never `🥁` which is a drum, not a metronome).
 */
/**
 * Passer d'une prise à l'autre, et rien de plus.
 *
 * # Pourquoi ici
 *
 * Un replay ne garde presque rien de la barre — ni tempo, ni métronome, ni
 * tonalité : sa grille est celle du récital, et son ton n'a pas été lu. Reste
 * le transport, et passer d'une prise à l'autre en est un geste : c'est la même
 * musique, jouée une autre fois.
 *
 * # Le même endroit, d'une prise à l'autre
 *
 * Changer de prise garde la tête où elle est. Comparer deux essais, c'est
 * écouter le même passage dans l'un puis dans l'autre ; revenir au début à
 * chaque fois obligerait à retrouver l'endroit à l'oreille.
 *
 * # Ce qui en est parti (2026-09-22)
 *
 * **Publier** : c'est un geste qu'on fait en regardant ce qu'on publie, à côté
 * de ce qu'on supprime et du lien qu'on va coller — il est dans le widget des
 * prises, sur le rouleau (`replay-takes-widget.tsx`). Il y a quelque chose à
 * lire avant de le faire, et une barre d'outils n'est pas un endroit où l'on
 * lit.
 *
 * **Le MIDI** : retiré à la demande de l'utilisateur. Un bouton de la barre
 * proposait d'emporter la prise dans un séquenceur ; ce n'est pas ce qu'on
 * vient faire ici, et le fichier reste au dépôt pour l'analyse.
 *
 * # Et les faux départs n'y sont pas non plus
 *
 * Le sélecteur ne montre que les prises **jouables** : un essai à zéro note n'a
 * pas de morceau à ouvrir. Il se voit, et se supprime, dans le widget.
 */
function TakePicker() {
  const replay = useReplay();
  const playable = replay.takes.filter((take) => take.playable);

  if (playable.length < 2) {
    return null;
  }

  const index = playable.findIndex((take) => take.number === replay.current);
  const take = playable[index] ?? null;

  const step = (by: number) => {
    const next = playable[index + by];

    if (next !== undefined) {
      replay.goToTake(next.number);
    }
  };

  return (
    <div className="flex items-center gap-1 mr-1">
      <IconButton
        variant="ghost"
        size="sm"
        title="Prise précédente"
        onClick={() => step(-1)}
        disabled={index <= 0}
      >
        <ArrowLeftIcon size={14} />
      </IconButton>

      <span className="text-[12px] font-medium tabular-nums text-text-secondary whitespace-nowrap">
        {take?.label ?? "Prise"}
        <span className="text-text-muted"> sur {playable.length}</span>
      </span>

      <IconButton
        variant="ghost"
        size="sm"
        title="Prise suivante"
        onClick={() => step(1)}
        disabled={index < 0 || index >= playable.length - 1}
      >
        <ArrowLeftIcon size={14} className="rotate-180" />
      </IconButton>
    </div>
  );
}

function MetronomeIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 4h10l3 16H4L7 4z" />
      <path d="M9 10l5 6" />
    </svg>
  );
}

export const Toolbar = memo(function Toolbar({
  songInfo,
  sourceTonality,
  onTranspose,
  isGenerated,
  onBack,
  onOpenSettings,
  onEnterProgram,
  sidebarOpen,
  onToggleSidebar,
  livePanelOpen,
  onToggleLivePanel,
  spectator = false,
}: ToolbarProps) {
  const { studio } = useStudio();
  const [engagement, setEngagement] = useStudioEngagement();
  const [tonality, setTonality] = useTonality();
  const [bpm, setBpm] = useBpm();
  const mic = useMicToggle();
  const { devices: midiDevices } = useMidiInputs();
  const activeMidiDevice = midiDevices.find((d) => d.connected && d.enabled) ?? null;
  const midi = useMidiToggle();
  const [keyboardEnabled, setKeyboardEnabled] = useKeyboardEnabled();
  const [metronomeEnabled, setMetronomeEnabled] = useMetronomeEnabled();
  const [signature, setSignature] = useTimeSignature();
  const [division, setDivision] = useMetronomeDivision();
  const [accent, setAccent] = useMetronomeAccent();
  const [metronomeVolume, setMetronomeVolume] = useMetronomeVolume();
  const localPlaying = usePlaybackState();
  const live = useLiveSession();
  const replay = useReplay();

  // En séance, lecture et pause sont celles de la séance, pour qui la pilote :
  // la vue locale n'a pas de lecture à elle, elle suit. L'interprète d'un
  // récital met ainsi son public en pause d'ici. Le spectateur n'a pas ces
  // commandes du tout.
  const liveControl = live !== null
    && (live.status === "live" || live.status === "reconnecting")
    && !live.isListener;
  // Un replay est un morceau chargé : sa lecture est celle du studio, et rien
  // ici n'a à la connaître. Voir `contexts/replay-context.tsx`.
  const replaying = replay.recitalId !== null;
  const isPlaying = liveControl ? live.sessionState?.paused === false : localPlaying;

  /**
   * La poignée du tiroir, rendue au spectateur d'un replay — et à lui seul.
   *
   * La barre latérale d'un replay porte les **prises**, et un spectateur n'a
   * autrement aucun chemin vers elles. Mais seulement là où elle est un
   * tiroir : sur un téléphone, couché ou debout, où elle part repliée
   * (voir `studio-page.tsx`). Sur un grand écran elle est en flux et déjà
   * ouverte — il n'y aurait rien à déplier, et le bouton ne ferait que
   * déplacer une barre d'outils qui ne doit pas bouger d'un pixel.
   *
   * En JS et non en CSS, contrairement au reste de ce fichier : `hidden`
   * l'emporte sur le `inline-flex` d'`IconButton`, donc masquer marche, mais
   * la règle inverse — faire réapparaître sous requête média ce qui est caché
   * par défaut — dépendrait de l'ordre d'émission des utilitaires de Tailwind.
   * Le hook répond `false` au premier rendu, ce qui est ici le bon sens de
   * l'erreur : le bureau ne voit jamais passer ce bouton.
   *
   * Et seulement si la prise a été analysée : sans analyse la barre latérale
   * ne se monte pas du tout (`sidebarShown`), et une poignée qui n'ouvre rien
   * est pire qu'une poignée absente.
   *
   * Sur **tout** écran, et pas seulement sur téléphone comme d'abord écrit :
   * devant un replay, un grand écran n'avait alors plus rien pour replier une
   * barre qui lui prend deux cents pixels. La raison de la restreindre était
   * qu'un bouton de plus déplace la barre d'outils de 34 px — mais ce
   * déplacement-là n'existe que pour un morceau ordinaire, où la poignée est
   * déjà là.
   */
  const replayDrawerHandle = replaying && replay.analyzed;

  const [tempoOpen, setTempoOpen] = useState(false);

  const tempoAnchorRef = useRef<HTMLButtonElement>(null);

  // When a piece is loaded, the metronome runs in the song's signature , 
  // the picker is read-only and the value is sourced from `songInfo`. When
  // unloaded, the user-chosen signature in the studio takes over. We push
  // the effective signature into the studio so the running metronome stays
  // accurate; on unload, the last applied signature naturally remains until
  // the user picks a new one.
  const lockedSignature = songInfo?.timeSignatureSig ?? null;
  const effectiveSignature: Signature = lockedSignature ?? signature;

  useEffect(() => {
    if (!studio || !lockedSignature) {
      return;
    }

    studio.continuousMetronome.setSignature(lockedSignature);
  }, [studio, lockedSignature]);

  const hasSong = songInfo !== null && songInfo !== undefined;
  // Le parcours par cœur mène son propre transport depuis ses prises, donc les
  // commandes centrales se retirent devant lui. Le parcours harmonique, lui,
  // les garde : le transport y est la tête de lecture du niveau. Le spectateur
  // d'un récital n'a pas de transport à lui du tout.
  // Un spectateur n'a pas de transport à lui — sauf devant un replay, où ce
  // qu'il regarde est un morceau, et le transport celui du morceau.
  const showTransportControls =
    engagement?.kind !== "byHeart" && (!spectator || replaying);
  // The mode toggle (Apprendre / Libre) is the way in and out of
  // the parcours, so it must stay visible there too. Only gated
  // on having a piece loaded — and on not being a spectator.
  const showSongControls = hasSong && !spectator;

  const handlePlayPause = useCallback(() => {
    if (liveControl) {
      void live.setTransportPaused(isPlaying);

      return;
    }

    if (!studio) {
      return;
    }

    const transport = studio.getLocalTransport();

    if (studio.countIn.active) {
      studio.countIn.cancel();

      return;
    }

    if (transport.isDetached) {
      // C'est ici, et nulle part ailleurs, qu'une lecture est *demandée* : le
      // transport, lui, ne sait pas qui l'a lancé, et le jingle d'accueil, la
      // première note du jeu libre et les passes d'un parcours empruntent le
      // même `play()`.
      //
      // Et elle ne se compte qu'une fois le décompte passé : celui-ci met le
      // transport en pause pour s'armer, et cette pause-là refermerait aussitôt
      // le segment de temps joué — compter le départ avant lui rendrait
      // `playedMs` nul à chaque lecture. Un décompte annulé laisse le transport
      // en pause, donc ne compte pas.
      void studio.startWithCountIn().then(() => {
        if (!studio.getLocalTransport().isDetached) {
          recordPlay();
        }
      });

      return;
    }

    transport.detach();
  }, [studio, liveControl, live, isPlaying]);

  const handleStop = useCallback(() => {
    // En séance, arrêter ne ramène personne au début : l'interprète joue au
    // direct, et revenir en arrière est un geste de spectateur. Stop fige.
    if (liveControl) {
      void live.setTransportPaused(true);

      return;
    }

    if (!studio) {
      return;
    }

    const transport = studio.getLocalTransport();
    transport.detach();
    transport.seek(0);
    studio.countIn.cancel();
  }, [studio, liveControl, live]);

  const handleModeChange = useCallback(
    (next: StudioModeValue) => {
      // « Apprendre » ouvre l'entrée du programme et n'engage rien.
      //
      // Les deux parcours partagent cette porte, et lequel s'engage dépend du
      // choix. Le trancher ici trancherait à la place du joueur, et la bascule
      // resterait sur « Libre » tant qu'il n'a pas choisi, ce qui est
      // exactement l'état où il se trouve.
      //
      // L'entrée n'est pas un écran d'ici mais la fiche du morceau, posée sur
      // son choix de parcours. Cette barre ne sait pas la monter : c'est la
      // page qui la tient, et elle passe le geste.
      if (next === "learn") {
        onEnterProgram?.();

        return;
      }

      // « Libre » relâche l'engagement et garde la vue courante : l'engagement
      // est *ce qu'on travaille*, la vue est *ce qu'on regarde*.
      setEngagement(null);
    },
    [setEngagement, onEnterProgram],
  );

  const handleBpmChange = useCallback(
    (newBpm: number) => {
      setBpm(newBpm);
    },
    [setBpm],
  );

  const handleMetronomeToggle = useCallback(
    (enabled: boolean) => {
      setMetronomeEnabled(enabled);
    },
    [setMetronomeEnabled],
  );

  const originalBpm = songInfo?.bpm ?? 120;
  // La bascule ne connaît que deux positions, et l'engagement en a trois : les
  // deux parcours se lisent tous les deux « Apprendre ». C'est ce que la
  // bascule dit depuis toujours, et lequel des deux est en cours se lit sur
  // l'écran, pas sur un onglet de barre.
  const modeValue: StudioModeValue = engagement === null ? "free" : "learn";

  return (
    /* 56 px de toolbar + 48 px de header, c'était 28 % d'un iPhone SE en
       paysage (667×375) avant même le clavier. En viewport court on descend à
       44 px, le plancher imposé par les boutons de transport (36 px) : tout
       ce qu'on gagne retourne au piano-roll. Seuil partagé avec le header,
       voir `useShortViewport`. */
    <div className="flex items-center h-14 [@media(max-height:480px)]:h-11 px-4 [@media(max-height:480px)]:px-2 bg-bg-surface border-b border-border select-none">
      {/* ── Toolbar layout ──
          LEFT   : tonality (+ back / badge / mode toggle if a piece is loaded)
          CENTER : transport · tempo · metronome (always, also available in free play)
          RIGHT  : device status · settings
      */}
      {/* À 667 px de large — la fenêtre la plus étroite servie — les trois
          groupes réclament 730 px pour 635 px utiles. Les contrôles étant
          devenus incompressibles (sans quoi ils se replient hors de leur
          hauteur fixe), le déficit se comble en retirant les annotations, pas
          les commandes : l'espacement se resserre. */}
      <div className="flex items-center gap-2.5 [@media(max-height:480px)]:gap-1.5 min-w-0">
        {onToggleSidebar && (!spectator || replayDrawerHandle) && (
          <IconButton
            variant="ghost"
            size="sm"
            active={sidebarOpen}
            aria-expanded={sidebarOpen}
            aria-controls="studio-sidebar"
            title={sidebarOpen ? "Masquer le panneau ([)" : "Afficher le panneau ([)"}
            onClick={onToggleSidebar}
          >
            {sidebarOpen ? (
              <PanelLeftCloseIcon size={14} />
            ) : (
              <PanelLeftOpenIcon size={14} />
            )}
          </IconButton>
        )}

        {hasSong && isGenerated && onBack && !spectator && (
          <IconButton variant="ghost" title="Revenir au morceau" onClick={onBack} size="sm">
            <ArrowLeftIcon size={14} />
          </IconButton>
        )}

        {/* Devant un replay, le ton ne s'affiche que s'il a été lu : une prise
            analysée en a un comme n'importe quelle pièce, une prise rebâtie
            depuis les journaux n'en a aucun, et l'annoncer serait inventer. */}
        {(!replaying || replay.analyzed) && (
          <TonalityPicker
            className={replaying ? HANDHELD_HIDDEN : undefined}
            tonality={tonality}
            sourceTonality={sourceTonality}
            onPick={setTonality}
            onTranspose={onTranspose}
          />
        )}

        {showSongControls && (
          <div className="ml-2">
            <StudioModeToggle value={modeValue} onChange={handleModeChange} />
          </div>
        )}
      </div>

      <div className="flex-1" />

      {showTransportControls && (
        <>
          <div className="flex items-center gap-2">
            {/* Les prises se choisissent ici, contre le transport : passer de
                l'une à l'autre est un geste de lecture, et la barre est le seul
                endroit qu'un replay garde. */}
            <TakePicker />

            <TransportControls
              isPlaying={isPlaying}
              onPlayPause={handlePlayPause}
              onStop={handleStop}
            />

            {/* Ni tempo ni métronome devant un replay : la grille est celle
                du récital enregistré, et la déplacer ferait tomber les notes
                ailleurs qu'où elles ont été jouées. */}
            {!replaying && (
              <>
                <button
                  ref={tempoAnchorRef}
                  type="button"
                  onClick={() => setTempoOpen((v) => !v)}
                  aria-haspopup="dialog"
                  aria-expanded={tempoOpen}
                  title="Tempo et métronome"
                  /* Même extension de cible au doigt que les `IconButton` en
                     `sm` : le bouton est large mais fait 28 px de haut. */
                  className="relative ml-1 inline-flex items-center h-7 px-2.5 rounded-md text-[12px] font-medium tracking-wide text-text-secondary hover:text-text-primary hover:bg-white/[0.04] transition-colors duration-150 cursor-pointer pointer-coarse:after:absolute pointer-coarse:after:content-[''] pointer-coarse:after:-inset-y-2 pointer-coarse:after:inset-x-0"
                >
                  <span className="mr-1.5 text-text-muted">♩=</span>
                  <span className="tabular-nums">{bpm}</span>
                </button>

                <IconButton
                  variant="ghost"
                  active={metronomeEnabled}
                  title={metronomeEnabled ? "Métronome activé" : "Métronome désactivé"}
                  size="sm"
                  onClick={() => handleMetronomeToggle(!metronomeEnabled)}
                >
                  <MetronomeIcon size={14} />
                </IconButton>
              </>
            )}
          </div>

          <div className="flex-1" />
        </>
      )}

      <div className="flex items-center gap-2">
        {/* Le MIDI reste sur un téléphone couché, le micro et le clavier
            d'ordinateur s'en vont : un clavier MIDI se branche en Bluetooth sur
            un téléphone et redonne un instrument devant l'enregistrement,
            tandis qu'un micro qui écoute la pièce et un clavier AZERTY qui
            n'existe pas n'y ouvrent rien. */}
        <div className="flex items-center gap-1.5">
          <DeviceStatusBadge
            type="mic"
            state={mic.state}
            onClick={() => void mic.toggle()}
            className={replaying ? HANDHELD_HIDDEN : undefined}
          />
          <DeviceStatusBadge
            type="midi"
            state={midi.state}
            deviceName={activeMidiDevice?.name}
            onClick={() => void midi.toggle()}
          />
          <DeviceStatusBadge
            type="keyboard"
            state={keyboardEnabled ? "on" : "off"}
            onClick={() => setKeyboardEnabled(!keyboardEnabled)}
            className={replaying ? HANDHELD_HIDDEN : undefined}
          />
        </div>

        {/* Les trois lisent le contexte live et se retirent d'eux-mêmes hors
            live : la barre n'a rien à savoir du live. « Revenir au live » sert
            au spectateur, qui se promène dans le récital ; l'indicateur ne se
            montre que pendant un live, et le motif d'une jointure ratée prend
            sa place quand il y en a un. */}
        <BackToLiveButton />
        <LiveJoinError />
        {/* Hors séance, la place de l'indicateur de live : s'enregistrer,
            inviter, rejoindre — le widget « Seul ou à plusieurs » part d'ici. */}
        <LobbyButton />
        <GoLiveButton
          panelOpen={livePanelOpen ?? false}
          onTogglePanel={onToggleLivePanel ?? (() => undefined)}
        />

        <IconButton
          variant="ghost"
          title="Paramètres"
          size="sm"
          onClick={onOpenSettings}
        >
          <SettingsIcon size={14} />
        </IconButton>
      </div>

      {/* Sans son bouton, la bulle n'a plus d'ancre ni de raison d'être :
          elle part avec lui plutôt que de rester montée fermée. */}
      {!spectator && (
        <TempoMetronomePopover
          anchorRef={tempoAnchorRef}
          open={tempoOpen}
          onClose={() => setTempoOpen(false)}
          bpm={bpm}
          onBpmChange={handleBpmChange}
          originalBpm={originalBpm}
          metronomeEnabled={metronomeEnabled}
          onMetronomeToggle={handleMetronomeToggle}
          metronomeVolume={metronomeVolume}
          onMetronomeVolumeChange={setMetronomeVolume}
          signature={effectiveSignature}
          onSignatureChange={setSignature}
          signatureLocked={lockedSignature !== null}
          division={division}
          onDivisionChange={setDivision}
          accent={accent}
          onAccentChange={setAccent}
        />
      )}
    </div>
  );
});
